import React, { useState, useRef } from "react";
import { db } from "../../config/firebase";
import { collection, setDoc, doc, Timestamp } from "firebase/firestore";
import * as XLSX from "xlsx";
import moment from "moment";

function DbUpdates() {
  const [collectionName, setCollectionName] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // null, 'success', 'error'
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentRecord, setCurrentRecord] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const fileInputRef = useRef(null);

  const handleCollectionNameChange = (event) => {
    setCollectionName(event.target.value);
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return null;

    // If it's already a Date object, return it
    if (dateValue instanceof Date) {
      return Timestamp.fromDate(dateValue);
    }

    // If it's a number (Excel date serial), convert it
    if (typeof dateValue === "number") {
      // Excel's date system: January 1, 1900 is day 1, but Excel incorrectly treats 1900 as a leap year
      // To get the correct date, we need to account for this
      const excelEpoch = new Date(1900, 0, 1); // January 1, 1900
      const millisecondsInDay = 86400000;

      // Subtract 2 to account for Excel's leap year bug and 0-based indexing
      const adjustedDays = dateValue - 2;
      const date = new Date(
        excelEpoch.getTime() + adjustedDays * millisecondsInDay
      );

      // Ensure we're working with the exact date without timezone issues
      const utcDate = new Date(
        Date.UTC(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          12,
          0,
          0,
          0 // Set to noon UTC to avoid timezone edge cases
        )
      );

      return Timestamp.fromDate(utcDate);
    }

    // If it's a string, try parsing it with moment.js
    if (typeof dateValue === "string") {
      // Handle empty strings
      if (dateValue.trim() === "") return null;

      const formats = [
        "YYYY-MM-DD",
        "MM/DD/YYYY",
        "DD/MM/YYYY",
        "YYYY-MM-DD HH:mm:ss",
        "MM/DD/YYYY HH:mm:ss",
        "MM-DD-YYYY",
        "DD-MM-YYYY",
        "YYYY/MM/DD",
        "M/D/YYYY",
        "M-D-YYYY",
        "M/D/YY",
        "MM-DD-YY",
        "DD-MM-YY",
      ];

      let formattedDate = moment(dateValue, formats, true);

      // If strict parsing fails, try lenient parsing
      if (!formattedDate.isValid()) {
        formattedDate = moment(dateValue);
      }

      if (formattedDate.isValid()) {
        // Set time to noon to avoid timezone issues
        formattedDate.utc().hour(12).minute(0).second(0).millisecond(0);
        return Timestamp.fromDate(formattedDate.toDate());
      }
    }

    console.warn(
      "Failed to parse date value:",
      dateValue,
      "Type:",
      typeof dateValue
    );
    return null;
  };

  const handleUpload = async () => {
    if (!collectionName || !file) {
      setUploadStatus("error");
      alert("Please enter a collection name and select a file.");
      return;
    }

    setUploading(true);
    setUploadStatus(null);
    setStartTime(Date.now());

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0]; // Assuming you want the first sheet
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

        // Extract headers (first row)
        const headers = jsonData[0];

        // Process data rows (excluding the header row)
        const dataRows = jsonData.slice(1);

        // Set total records for progress tracking
        setTotalRecords(dataRows.length);
        setCurrentRecord(0);
        setUploadProgress(0);

        const collectionRef = collection(db, collectionName);

        for (let index = 0; index < dataRows.length; index++) {
          const row = dataRows[index];
          const docData = {};

          // Update progress before processing each record
          const recordsCompleted = index + 1;
          setCurrentRecord(recordsCompleted);
          setUploadProgress((recordsCompleted / dataRows.length) * 100);

          for (let i = 0; i < headers.length; i++) {
            const headerName = headers[i].toLowerCase();
            let value = row[i];

            // Handle NULL, undefined, and empty values
            if (value === "NULL" || value === null || value === undefined) {
              value = "";
            }

            const specificDateHeaders = [
              "application_dt", // Ensure these are lowercase to match headerName
              "dob",
              "last_updated",
              "added_to_portal",
              "created_at", // Add created_at to be recognized as a date field
              "time_approved",
              "begdate", // Add BegDate (lowercase for matching)
              "enddate", // Add EndDate (lowercase for matching)
              "time_updated_stamp", // Add Time_Updated_Stamp (lowercase for matching)
              "ctan_earned_date", // Add CTAN_Earned_Date (lowercase for matching)
            ];

            // Handle Date values
            // Check if the header contains "date" OR "deadline" (case-insensitive)
            if (
              headerName.includes("date") || // General check for "date"
              headerName.includes("expiredate") || // General check for "expiredate"
              specificDateHeaders.includes(headerName)
            ) {
              const originalValue = value;
              value = formatDate(value);
              console.log(`Date conversion for field '${
                headers[i]
              }' (${headerName}):
                Original value: ${originalValue} (${typeof originalValue})
                Converted value: ${value ? "Timestamp" : "null"}`);
            }

            docData[headers[i]] = value;
          }

          // Generate a unique ID for each document
          const newDocRef = doc(collectionRef);
          await setDoc(newDocRef, docData); // Use setDoc to create a document with a specific ID

          // Force UI update every 10 records for better responsiveness
          if (index % 10 === 0 || index === dataRows.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 1));
          }
        }

        setUploadStatus("success");

        // Keep progress visible for 2 seconds to show completion
        setTimeout(() => {
          alert("File uploaded successfully!");
          setFile(null);
          setCollectionName("");
          setTotalRecords(0);
          setCurrentRecord(0);
          setUploadProgress(0);
        }, 2000);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      };
      reader.onerror = (error) => {
        console.error("Error reading file:", error);
        setUploadStatus("error");
        alert("Error reading file.");
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Error uploading data:", error);
      setUploadStatus("error");
      alert("Error uploading data.");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setCollectionName("");
    setUploadStatus(null);
    setTotalRecords(0);
    setCurrentRecord(0);
    setUploadProgress(0);
    setStartTime(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Calculate estimated time remaining
  const getEstimatedTimeRemaining = () => {
    if (!startTime || currentRecord === 0) return null;

    const elapsedTime = Date.now() - startTime;
    const recordsPerMs = currentRecord / elapsedTime;
    const remainingRecords = totalRecords - currentRecord;
    const estimatedRemainingMs = remainingRecords / recordsPerMs;

    const minutes = Math.floor(estimatedRemainingMs / 60000);
    const seconds = Math.floor((estimatedRemainingMs % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <div>
      <h2>Upload CSV to Firebase</h2>
      <div>
        <label htmlFor="collectionName">Collection Name:</label>
        <input
          type="text"
          id="collectionName"
          value={collectionName}
          onChange={handleCollectionNameChange}
          disabled={uploading}
        />
      </div>
      <div>
        <label htmlFor="fileUpload">Upload CSV:</label>
        <input
          type="file"
          id="fileUpload"
          accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
          onChange={handleFileChange}
          disabled={uploading}
          ref={fileInputRef}
        />
      </div>
      <div>
        <button onClick={handleUpload} disabled={uploading}>
          {uploading ? "Uploading..." : "Upload"}
        </button>
        <button onClick={handleClear} disabled={uploading}>
          Clear
        </button>
      </div>

      {/* Progress Section */}
      {uploading && totalRecords > 0 && (
        <div
          style={{
            marginTop: "30px",
            padding: "25px",
            backgroundColor: "#f8f9fa",
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <div style={{ marginBottom: "20px", textAlign: "center" }}>
            <strong style={{ fontSize: "20px", color: "#1976d2" }}>
              📤 Upload Progress
            </strong>
          </div>

          <div style={{ marginBottom: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <span
                style={{ fontSize: "16px", color: "#333", fontWeight: "500" }}
              >
                Processing Records
              </span>
              <span
                style={{
                  fontSize: "16px",
                  color: "#1976d2",
                  fontWeight: "bold",
                }}
              >
                {currentRecord.toLocaleString()} of{" "}
                {totalRecords.toLocaleString()}
              </span>
            </div>

            {getEstimatedTimeRemaining() && (
              <div
                style={{
                  fontSize: "14px",
                  color: "#666",
                  textAlign: "center",
                  marginBottom: "10px",
                }}
              >
                Estimated time remaining: {getEstimatedTimeRemaining()}
              </div>
            )}
          </div>

          <div
            style={{
              width: "100%",
              backgroundColor: "#f5f5f5",
              borderRadius: "12px",
              overflow: "hidden",
              height: "20px",
              marginBottom: "15px",
              border: "1px solid #e0e0e0",
            }}
          >
            <div
              style={{
                width: `${uploadProgress}%`,
                backgroundColor:
                  currentRecord === totalRecords ? "#4caf50" : "#1976d2",
                height: "100%",
                transition: "width 0.2s ease, background-color 0.3s ease",
                borderRadius: "12px",
                background:
                  currentRecord === totalRecords
                    ? "linear-gradient(90deg, #4caf50, #66bb6a)"
                    : "linear-gradient(90deg, #1976d2, #42a5f5)",
              }}
            ></div>
          </div>

          <div
            style={{
              textAlign: "center",
              fontSize: "18px",
              fontWeight: "bold",
              color: currentRecord === totalRecords ? "#4caf50" : "#1976d2",
              marginBottom: "10px",
            }}
          >
            {Math.round(uploadProgress)}% Complete
          </div>

          {uploadProgress === 100 && (
            <div
              style={{
                textAlign: "center",
                marginTop: "15px",
                color: "#4caf50",
                fontWeight: "bold",
                fontSize: "16px",
                padding: "15px",
                backgroundColor: "#f1f8e9",
                borderRadius: "8px",
                border: "1px solid #c8e6c9",
              }}
            >
              ✅ Upload Complete! All {totalRecords.toLocaleString()} records
              processed successfully.
            </div>
          )}

          <div
            style={{
              marginTop: "15px",
              fontSize: "12px",
              color: "#999",
              textAlign: "center",
              fontStyle: "italic",
            }}
          >
            Please keep this window open until upload completes
          </div>
        </div>
      )}

      {uploadStatus === "success" && (
        <div style={{ color: "green" }}>Upload successful!</div>
      )}
      {uploadStatus === "error" && (
        <div style={{ color: "red" }}>Upload failed.</div>
      )}
    </div>
  );
}

export default DbUpdates;
